import React, { useState } from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2 } from 'lucide-react';

// 个人信息2表单 - 国籍和身份信息
const Personal2Form = ({ data, onChange }) => {

  // 身份证验证错误消息状态
  const [idNumberError, setIdNumberError] = useState('');
  // 身份证号提取的生日信息
  const [idBirthInfo, setIdBirthInfo] = useState('');

  // 身份证号验证函数（增强版：格式+校验码+生日）
  const validateIDNumber = (idNumber) => {
    if (!idNumber) {
      setIdBirthInfo('');
      return '';
    }

    // 只支持18位身份证（15位旧版逐步淘汰）
    if (idNumber.length !== 18) {
      setIdBirthInfo('');
      return '身份证号长度不正确（应为18位）';
    }

    // 格式验证：17位数字 + 1位数字或X
    const pattern18 = /^\d{17}[\dXx]$/;
    if (!pattern18.test(idNumber)) {
      setIdBirthInfo('');
      return '身份证号格式不正确，应为17位数字+1位数字或X';
    }

    // 校验码验证
    const checkCodeValidation = validateCheckCode(idNumber);
    if (!checkCodeValidation.valid) {
      setIdBirthInfo('');
      return checkCodeValidation.error;
    }

    // 生日提取和验证
    const birthValidation = validateBirthDate(idNumber);
    if (!birthValidation.valid) {
      setIdBirthInfo('');
      return birthValidation.error;
    }

    // 设置生日信息显示
    setIdBirthInfo(birthValidation.info);

    return '';
  };

  // 校验码验证函数
  const validateCheckCode = (idNumber) => {
    const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    const checkCodes = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];

    let sum = 0;
    for (let i = 0; i < 17; i++) {
      sum += parseInt(idNumber[i]) * weights[i];
    }

    const expectedCheckCode = checkCodes[sum % 11];
    const actualCheckCode = idNumber[17].toUpperCase();

    if (expectedCheckCode !== actualCheckCode) {
      return {
        valid: false,
        error: `身份证号校验码错误！应为 ${expectedCheckCode}，实际为 ${actualCheckCode}`
      };
    }

    return { valid: true };
  };

  // 月份映射：英文缩写 ↔ 数字（兼容表单1的格式）
  const MONTH_MAP = {
    'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
    'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
    'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
  };

  // 生日验证函数
  const validateBirthDate = (idNumber) => {
    // 提取生日（第7-14位：YYYYMMDD）
    const birthStr = idNumber.substring(6, 14);
    const year = birthStr.substring(0, 4);
    const month = birthStr.substring(4, 6);
    const day = birthStr.substring(6, 8);

    // 验证日期合法性
    const birthDate = new Date(year, parseInt(month) - 1, day);
    if (birthDate.getFullYear() !== parseInt(year) ||
        birthDate.getMonth() !== parseInt(month) - 1 ||
        birthDate.getDate() !== parseInt(day)) {
      return {
        valid: false,
        error: `身份证号中的生日 ${year}-${month}-${day} 不是有效日期`
      };
    }

    // 合理性检查：年份范围（1900-当前年份）
    const currentYear = new Date().getFullYear();
    if (parseInt(year) < 1900 || parseInt(year) > currentYear) {
      return {
        valid: false,
        error: `身份证号中的出生年份 ${year} 不合理`
      };
    }

    // 尝试读取personal_1的生日数据进行对比
    let warningMsg = '';
    try {
      const personal1Data = formDataStorage.loadFormData('personal_1');
      if (personal1Data && personal1Data.date_and_place_of_birth) {
        const p1Birth = personal1Data.date_and_place_of_birth;
        const p1Year = p1Birth.year;
        // 月份格式转换：支持英文缩写（JAN-DEC）和数字格式
        const p1MonthRaw = p1Birth.month;
        const p1Month = MONTH_MAP[p1MonthRaw] || String(p1MonthRaw).padStart(2, '0');
        const p1Day = String(p1Birth.day).padStart(2, '0');

        // 对比生日（标准化后的格式）
        if (year !== String(p1Year) || month !== p1Month || day !== p1Day) {
          warningMsg = `\n⚠️ 警告：与表单1填写的生日（${p1Year}-${p1Month}-${p1Day}）不一致！`;
        }
      }
    } catch (e) {
      // 读取失败不影响验证
      console.log('无法读取personal_1数据，跳过生日对比');
    }

    return {
      valid: true,
      info: `📅 身份证生日：${year}年${parseInt(month)}月${parseInt(day)}日${warningMsg}`
    };
  };

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    const savedData = formDataStorage.loadFormData('personal_2');
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('Personal2Form useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]);
  
  // 更新其他国籍
  const updateOtherNationality = (index, field, value) => {
    const currentOtherNationalities = data.other_nationalities || [];
    const newOtherNationalities = [...currentOtherNationalities];
    if (!newOtherNationalities[index]) {
      newOtherNationalities[index] = { nationality: '', has_passport: false, passport_number: "" };
    }
    
    if (field === 'has_passport') {
      newOtherNationalities[index] = { 
        ...newOtherNationalities[index], 
        [field]: value,
        passport_number: value ? (newOtherNationalities[index].passport_number || '') : ""
      };
    } else {
      newOtherNationalities[index] = { ...newOtherNationalities[index], [field]: value };
    }
    
    onChange('other_nationalities', newOtherNationalities);
  };

  // 添加其他国籍
  const addOtherNationality = () => {
    const currentOtherNationalities = data.other_nationalities || [];
    const newOtherNationalities = [...currentOtherNationalities, { nationality: '', has_passport: false, passport_number: "" }];
    onChange('other_nationalities', newOtherNationalities);
  };

  // 删除其他国籍
  const removeOtherNationality = (index) => {
    const currentOtherNationalities = data.other_nationalities || [];
    const newOtherNationalities = currentOtherNationalities.filter((_, i) => i !== index);
    onChange('other_nationalities', newOtherNationalities);
    // 如果删除后没有国籍了，取消勾选状态
    if (newOtherNationalities.length === 0) {
      onChange('has_other_nationalities', false);
    }
  };

  // 更新永久居民国家
  const updatePermanentResidentCountry = (index, value) => {
    const currentCountries = data.permanent_resident_country || [];
    const newCountries = [...currentCountries];
    newCountries[index] = value;
    onChange('permanent_resident_country', newCountries.filter(country => country));
  };

  // 添加永久居民国家
  const addPermanentResidentCountry = () => {
    const currentCountries = data.permanent_resident_country || [];
    const newCountries = [...currentCountries, ''];
    onChange('permanent_resident_country', newCountries);
  };

  // 删除永久居民国家
  const removePermanentResidentCountry = (index) => {
    const currentCountries = data.permanent_resident_country || [];
    const newCountries = currentCountries.filter((_, i) => i !== index);
    onChange('permanent_resident_country', newCountries);
    // 如果删除后没有国家了，取消勾选状态
    if (newCountries.length === 0) {
      onChange('has_permanent_resident', false);
    }
  };

  // 更新美国社会安全号码
  const updateSSN = (partIndex, value) => {
    const currentSSN = data.us_social_security_number || ['', '', ''];
    const newSSN = [...currentSSN];
    newSSN[partIndex] = value;
    onChange('us_social_security_number', newSSN);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</span>
          国籍和身份信息
        </h3>

        {/* 基本国籍信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">所属国家/地区(国籍)</h4>
          <div className="grid grid-cols-1 gap-6">
            <CountrySelect
              label="国籍"
              value={data.country || ''}
              onChange={(value) => {
                onChange('country', value);
                onChange('national_id_not_applicable', false);
              }}
              required
            />
            
            {/* 身份证号码部分 */}
            <div className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400">
                            <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-700">身份证号码</h5>
                <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.national_id_not_applicable || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onChange('national_id_not_applicable', checked);
                        if (checked) {
                          onChange('national_identification_number', "");
                        }
                      }}
                      className="mr-2 w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      required
                    />
                    <span className="text-base font-medium text-gray-700">不适用</span>
                  </label>
                </div>
              </div>
              <FormField
                label="请输入身份证号码"
                value={data.national_identification_number || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onChange('national_identification_number', newValue);

                  // 实时验证身份证号
                  const errorMsg = validateIDNumber(newValue);
                  setIdNumberError(errorMsg);
                }}
                placeholder={data.national_id_not_applicable ? "已选择不适用，无法输入" : "请输入18位身份证号码"}
                disabled={data.national_id_not_applicable}
                error={idNumberError}
              />

              {/* 生日信息显示（身份证号验证通过后显示） */}
              {!data.national_id_not_applicable && data.national_identification_number && !idNumberError && idBirthInfo && (
                <div className={`mt-3 p-3 rounded-lg border-2 ${
                  idBirthInfo.includes('⚠️')
                    ? 'bg-yellow-50 border-yellow-400'
                    : 'bg-green-50 border-green-400'
                }`}>
                  <div className="flex items-start">
                    <span className={`text-2xl mr-2 ${idBirthInfo.includes('⚠️') ? '' : 'mt-1'}`}>
                      {idBirthInfo.includes('⚠️') ? '⚠️' : '✅'}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800 whitespace-pre-line">
                        {idBirthInfo}
                      </div>
                      {!idBirthInfo.includes('⚠️') && (
                        <div className="text-xs text-green-600 mt-1">
                          校验码验证通过
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 其他国籍管理 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">除了您上面填写的国籍外,您还拥有或曾拥有其它国籍吗?</h4>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="has_other_nationalities"
                  value="false"
                  checked={(data.has_other_nationalities !== undefined ? data.has_other_nationalities : false) === false}
                  onChange={(e) => {
                    onChange('has_other_nationalities', false);
                    onChange('other_nationalities', []);
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我没有其他国籍</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="has_other_nationalities"
                  value="true"
                  checked={(data.has_other_nationalities !== undefined ? data.has_other_nationalities : false) === true}
                  onChange={(e) => {
                    onChange('has_other_nationalities', true);
                    addOtherNationality();
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我有其他国籍</span>
              </label>
            </div>
          </div>

          {data.has_other_nationalities && (
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={addOtherNationality}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <Plus className="w-4 h-4 mr-1" />
                添加国籍
              </button>
            </div>
          )}

          {data.has_other_nationalities && (
            <div className="space-y-4">
              {(() => {
                const otherNationalities = data.other_nationalities || [];
                // 直接使用真实的数据，不再使用三元表达式
                return otherNationalities.map((nationality, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <CountrySelect
                          label={`其他国籍 ${index + 1}`}
                          value={nationality.nationality || ''}
                          onChange={(value) => {
                            updateOtherNationality(index, 'nationality', value)
                            if(!value){
                              onChange('has_other_nationalities', false)
                              onChange('other_nationalitied', [])
                            }
                          }}
                          required
                        />
                        <div className="flex items-center">
                          <label className="flex items-center mt-6">
                            <input
                              type="checkbox"
                              checked={nationality.has_passport || false}
                              onChange={(e) => updateOtherNationality(index, 'has_passport', e.target.checked)}
                              className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-sm font-medium text-gray-700">有护照</span>
                          </label>
                        </div>
                        {nationality.has_passport && (
                          <FormField
                            label="护照号码"
                            value={nationality.passport_number || ''}
                            onChange={(e) => updateOtherNationality(index, 'passport_number', e.target.value)}
                            placeholder="护照号码"
                          />
                        )}
                      </div>
                      {otherNationalities.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeOtherNationality(index)}
                          className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                          title="删除此国籍"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        {/* 永久居民身份 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">按上面所示的您的原籍国/地区（国籍），您现在是其它一个国家/地区的永久居民吗？</h4>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                <input
                  type="radio"
                  name="has_permanent_resident"
                  value="false"
                  checked={(data.has_permanent_resident !== undefined ? data.has_permanent_resident : false) === false}
                  onChange={(e) => {
                    onChange('has_permanent_resident', false);
                    onChange('permanent_resident_country', []);
                  }}
                  className="mr-3 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我不是永久居民</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                <input
                  type="radio"
                  name="has_permanent_resident"
                  value="true"
                  checked={(data.has_permanent_resident !== undefined ? data.has_permanent_resident : false) === true}
                  onChange={(e) => {
                    onChange('has_permanent_resident', true);
                    addPermanentResidentCountry();
                  }}
                  className="mr-3 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我是永久居民</span>
              </label>
            </div>
          </div>

          {data.has_permanent_resident && (
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={addPermanentResidentCountry}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <Plus className="w-4 h-4 mr-1" />
                添加国家
              </button>
            </div>
          )}

          {data.has_permanent_resident && (
            <div className="space-y-3">
              {(() => {
                const countries = data.permanent_resident_country || [];
                // 直接使用真实的数据，不再使用三元表达式
                return countries.map((country, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-green-50 rounded-lg border-l-4 border-green-400 shadow-sm">
                    <div className="flex-1">
                      <CountrySelect
                        label={`永久居民国家 ${index + 1}`}
                        value={country || ''}
                        onChange={(value) => updatePermanentResidentCountry(index, value)}
                      />
                    </div>
                    {countries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePermanentResidentCountry(index)}
                        className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                        title="删除此国家"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        {/* 美国相关信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h4 className="text-lg font-semibold text-gray-800 mb-6">美国相关信息</h4>
          
          {/* 美国社会安全号码 */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400">
                        <div className="flex items-center justify-between mb-4">
              <h5 className="text-md font-semibold text-gray-700">美国社会安全号码 (SSN)</h5>
              <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.ssn_not_applicable || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onChange('ssn_not_applicable', checked);
 
                    }}
                    className="mr-2 w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">不适用</span>
                </label>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {data.ssn_not_applicable ? '已选择不适用，无法输入' : '请分三部分输入您的美国社会安全号码'}
              </p>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  label="第一部分 (3位)"
                  value={(data.us_social_security_number && data.us_social_security_number[0]) || ''}
                  onChange={(e) => updateSSN(0, e.target.value)}
                  placeholder={data.ssn_not_applicable ? "已选择不适用，无法输入" : "000"}
                  maxLength={3}
                  disabled={data.ssn_not_applicable}
                />
                <FormField
                  label="第二部分 (2位)"
                  value={(data.us_social_security_number && data.us_social_security_number[1]) || ''}
                  onChange={(e) => updateSSN(1, e.target.value)}
                  placeholder={data.ssn_not_applicable ? "已选择不适用，无法输入" : "00"}
                  maxLength={2}
                  disabled={data.ssn_not_applicable}
                />
                <FormField
                  label="第三部分 (4位)"
                  value={(data.us_social_security_number && data.us_social_security_number[2]) || ''}
                  onChange={(e) => updateSSN(2, e.target.value)}
                  placeholder={data.ssn_not_applicable ? "已选择不适用，无法输入" : "0000"}
                  maxLength={4}
                  disabled={data.ssn_not_applicable}
                />
              </div>
              {!data.ssn_not_applicable && data.us_social_security_number && data.us_social_security_number.some(part => part) && (
                <div className="mt-3 p-2 bg-blue-100 rounded text-center">
                  <span className="text-sm text-blue-800 font-medium">
                    预览: {data.us_social_security_number.join('-')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 美国纳税人ID */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border-l-4 border-green-400">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-md font-semibold text-gray-700">美国纳税人ID (ITIN)</h5>
              <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.itin_not_applicable || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onChange('itin_not_applicable', checked);
                    }}
                    className="mr-2 w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">不适用</span>
                </label>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {data.itin_not_applicable ? '已选择不适用，无法输入' : '如果您有美国个人纳税人识别号码(ITIN)，请在此输入'}
              </p>
              <FormField
                label="美国纳税人ID"
                value={data.us_taxpayer_id || ''}
                onChange={(e) => onChange('us_taxpayer_id', e.target.value)}
                placeholder={data.itin_not_applicable ? "已选择不适用，无法输入" : "请输入美国纳税人ID (格式: XXX-XX-XXXX)"}
                disabled={data.itin_not_applicable}
              />
            </div>
          </div>
        </div>

        {/* 预览信息 */}
        {data.country && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <h4 className="font-semibold text-blue-800 mb-2">信息预览</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p><strong>国籍:</strong> {getCountryName(data.country)}</p>
              
              {/* 身份证预览 */}
              {data.national_id_not_applicable ? (
                <p><strong>身份证:</strong> <span className="text-red-600">不适用</span></p>
              ) : data.national_identification_number ? (
                <p><strong>身份证:</strong> {data.national_identification_number}</p>
              ) : ""}
              
              {/* 其他国籍预览 */}
              {data.has_other_nationalities && data.other_nationalities && data.other_nationalities.length > 0 && (
                <p><strong>其他国籍:</strong> {data.other_nationalities.length} 个</p>
              )}
              
              {/* 永久居民预览 */}
              {data.has_permanent_resident && data.permanent_resident_country && data.permanent_resident_country.length > 0 && (
                <p><strong>永久居民:</strong> {data.permanent_resident_country.map(country => getCountryName(country)).join(', ')}</p>
              )}
              
              {/* 美国SSN预览 */}
              {data.ssn_not_applicable ? (
                <p><strong>美国SSN:</strong> <span className="text-red-600">不适用</span></p>
              ) : data.us_social_security_number && data.us_social_security_number.some(part => part) ? (
                <p><strong>美国SSN:</strong> {data.us_social_security_number.join('-')}</p>
              ) : ""}
              
              {/* 美国纳税人ID预览 */}
              {data.itin_not_applicable ? (
                <p><strong>美国纳税人ID:</strong> <span className="text-red-600">不适用</span></p>
              ) : data.us_taxpayer_id ? (
                <p><strong>美国纳税人ID:</strong> {data.us_taxpayer_id}</p>
              ) : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Personal2Form; 
